import React, { useState, useEffect, useRef, useCallback } from 'react';

import { Text, Headline, Layout, ButtonGroup, TextLink } from '@oneaudi/unified-web-components';

import isMobile from 'ismobilejs';
import { UeElement } from '@oneaudi/falcon-tools';
import { ThemeProvider } from '@oneaudi/unified-web-common';
import {
  DIMENSION_M,
  DIMENSION_S,
  DIMENSION_XL,
  DIMENSION_XS,
  toSrc,
  toSrcSetPartial,
} from './utils/url-utils';
import { ParallaxTeaserProps, WltpProps } from './ComponentTypes';
import {
  LegalArea,
  LegalAreaItem,
  ParallaxArea,
  ParallaxTeaser,
  StyledImg,
  StyledLayout,
  StyledLayoutItem,
  StyledTextButton,
  WltpSpan,
  WltpWrapper,
} from './ParallaxTeaserStyles';
import { darkTheme, lightTheme } from '../../theme';
import { getThemeName } from '../../../alpha-helpers';

class Debouncer {
  private lastTimestamp = 0;

  public debounce(callback: () => void, wait: number) {
    const currentTimestamp = Date.now();

    if (currentTimestamp - this.lastTimestamp >= wait) {
      callback();
      this.lastTimestamp = currentTimestamp;
    }
  }
}

const WltpValuesComponent = ({
  formattedConsumption,
  formattedEmission,
  formattedCo2Class,
  formattedDischargedConsumption,
  formattedDischargedCo2Class,
}: WltpProps) => {
  return (
    <WltpWrapper>
      <WltpSpan data-testid="consumption">{formattedConsumption}</WltpSpan>
      <WltpSpan data-testid="emission">; {formattedEmission}</WltpSpan>
      {formattedCo2Class && <WltpSpan data-testid="co2Class">; {formattedCo2Class}</WltpSpan>}
      {formattedDischargedConsumption && (
        <WltpSpan data-testid="dischargedConsumption">; {formattedDischargedConsumption}</WltpSpan>
      )}
      {formattedDischargedCo2Class && (
        <WltpSpan data-testid="dischargedCo2Class">; {formattedDischargedCo2Class}</WltpSpan>
      )}
    </WltpWrapper>
  );
};

export const ParallaxTeaserComponent = (parallaxTeaserProps: ParallaxTeaserProps) => {
  const { headline, copy, links, images, linkTracking, legalData, linkType } = parallaxTeaserProps;
  const { imageXS, imageS, imageM, imageXL, altText = '' } = images;

  const [disableAnimation, setDisableAnimation] = useState(true);
  const parallaxRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const textboxRef = useRef<HTMLDivElement>(null);

  const imageScrollRate = 0.25;
  const textScrollRate = -0.125;
  const debouncer = new Debouncer();
  const [isMobileBrowser, setIsMobileBrowser] = useState(false);

  // Fix innerHeight for mobile browsers that hide url- and navigation bar, to avoid flickering on scroll.
  // let windowInnerHeight = window.innerHeight;
  const [windowInnerHeight, setWindowInnerHeight] = useState(0);
  let teaserDOMRect: DOMRect;

  function updateTeaserDOMRect() {
    if (!parallaxRef.current) {
      throw new Error(`The Teaser element could not be found!`);
    }
    teaserDOMRect = parallaxRef.current.getBoundingClientRect();
  }

  function getTeaserDOMRect() {
    if (!teaserDOMRect) {
      updateTeaserDOMRect();
    }
    return teaserDOMRect;
  }

  function isScrolledNearView(): boolean {
    const rect = getTeaserDOMRect();
    const topIsNearView = rect.top / 2 < window.innerHeight;
    const bottomIsNearView = rect.bottom > window.innerHeight / -2;
    return topIsNearView && bottomIsNearView;
  }

  function updateImageTop() {
    if (imageRef.current) {
      imageRef.current.style.top = `${(windowInnerHeight * imageScrollRate) / -2}px`;
    }
  }

  function updateTextboxTop() {
    if (parallaxRef.current && textboxRef.current) {
      const teaserRect = parallaxRef.current.getBoundingClientRect();
      const offsetToCenterTeaser = (windowInnerHeight - teaserRect.height) / 2;
      const isWindowSmallerThanTeaser = windowInnerHeight <= teaserRect.height;

      if (isWindowSmallerThanTeaser) {
        /**
         * If the window is smaller than the teaser, the textbox position will be at the
         * top of the window if the teaser is scrolled to its center.
         * The factor 2 is an additional visual ajustment.
         * */
        textboxRef.current.style.top = `${
          (-offsetToCenterTeaser - offsetToCenterTeaser * textScrollRate) / 2
        }px`;
      } else {
        textboxRef.current.style.top = `${-offsetToCenterTeaser * textScrollRate}px`;
      }
    }
  }

  function updateParallaxImageTransform(transformY: number) {
    // eslint-disable-next-line no-unused-expressions
    if (imageRef.current) {
      imageRef.current.style.transform = `translate3d(-50%, ${transformY}px, 0)`;
    }
  }

  function updateParallaxTextTransform(transformY: number) {
    if (textboxRef.current) {
      textboxRef.current.style.transform = `translate3d(0, ${transformY}px, 0)`;
    }
  }

  const updatePosition = useCallback(() => {
    const rect = getTeaserDOMRect();
    const offset = windowInnerHeight - rect.bottom;

    updateParallaxImageTransform(offset * imageScrollRate);
    updateParallaxTextTransform(offset * textScrollRate);
  }, [windowInnerHeight]);

  function resetPosition() {
    updateParallaxImageTransform(0);
    updateParallaxTextTransform(0);
  }

  function update() {
    updateTeaserDOMRect();
    if (isScrolledNearView()) {
      window.requestAnimationFrame(updatePosition);
    }
  }

  const onScroll = useCallback(() => {
    debouncer.debounce(update, 10);
  }, [windowInnerHeight]);

  let orientationChanged = false;
  const onWindowResize = useCallback(() => {
    if (!isMobileBrowser || orientationChanged) {
      setWindowInnerHeight(window.innerHeight);
      orientationChanged = false;
    }
    debouncer.debounce(() => {
      updateImageTop();
      updateTextboxTop();
    }, 10);
    updatePosition();
  }, [isMobileBrowser, windowInnerHeight]);

  const onOrientationChange = useCallback(() => {
    orientationChanged = true;
  }, []);

  function addEventListener() {
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onWindowResize, { passive: true });
    window.addEventListener('orientationchange', onOrientationChange);
  }

  function removeEventListener() {
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', onWindowResize);
    window.removeEventListener('orientationchange', onOrientationChange);
  }

  function addMediaQueryEventListener(
    mediaQuery: MediaQueryList,
    // eslint-disable-next-line no-unused-vars
    listener: (e: MediaQueryListEvent) => void,
  ) {
    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', listener, { passive: true });
    } else {
      // Fallback for older Browser e.g. Safari < v5
      mediaQuery.addListener(listener);
    }
  }

  function removeMediaQueryEventListener(
    mediaQuery: MediaQueryList,
    // eslint-disable-next-line no-unused-vars
    listener: (e: MediaQueryListEvent) => void,
  ) {
    if (typeof mediaQuery.removeEventListener === 'function') {
      mediaQuery.removeEventListener('change', listener);
    } else {
      // Fallback for older Browser e.g. Safari < v5
      mediaQuery.removeListener(listener);
    }
  }

  function updateDisableAnimation(mediaQuery: MediaQueryList | MediaQueryListEvent) {
    setDisableAnimation(mediaQuery.matches);
  }

  // reduce motion
  useEffect(() => {
    if (isMobile(window?.navigator).any) {
      setIsMobileBrowser(true);
    }
    setWindowInnerHeight(window.innerHeight);

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    updateDisableAnimation(mediaQuery);

    addMediaQueryEventListener(mediaQuery, updateDisableAnimation);

    return () => {
      removeMediaQueryEventListener(mediaQuery, updateDisableAnimation);
    };
  }, []);

  // reduce motion
  useEffect(() => {
    updateImageTop();
  }, [windowInnerHeight]);

  useEffect(() => {
    updateTextboxTop();

    if (disableAnimation) {
      removeEventListener();
      resetPosition();
    } else {
      addEventListener();
      updatePosition();
    }

    return () => removeEventListener();
  }, [disableAnimation, windowInnerHeight]);

  const theme = legalData.theme === 'dark' ? darkTheme : lightTheme;
  const gap = '24px';

  return (
    <ParallaxTeaser data-testid="parallaxTeaserId">
      <ParallaxArea ref={parallaxRef}>
        <picture>
          <UeElement type="media" property="images_imageXL" label="Image XL">
            {(ueProps) => (
              <source
                media="(min-width:1440px)"
                srcSet={toSrcSetPartial(imageXL, DIMENSION_XL.width)}
                sizes="(min-width:1920px) 1920px, 100vw"
                {...DIMENSION_XL}
                {...ueProps}
              />
            )}
          </UeElement>
          <UeElement type="media" property="images_imageM" label="Image M">
            {(ueProps) => (
              <source
                media="(min-width:768px)"
                srcSet={toSrcSetPartial(imageM, DIMENSION_M.width)}
                sizes="100vw"
                {...DIMENSION_M}
                {...ueProps}
              />
            )}
          </UeElement>
          <UeElement type="media" property="images_imageS" label="Image S">
            {(ueProps) => (
              <source
                media="(min-width:375px)"
                srcSet={toSrcSetPartial(imageS, DIMENSION_S.width)}
                sizes="100vw"
                {...DIMENSION_S}
                {...ueProps}
              />
            )}
          </UeElement>
          <UeElement type="media" property="images_imageXS" label="Image XS">
            {(ueProps) => (
              <StyledImg
                ref={imageRef}
                data-testid="image"
                alt={altText}
                loading="lazy"
                src={toSrc(imageXS, DIMENSION_XS.width)}
                srcSet={toSrcSetPartial(imageXS, DIMENSION_XS.width)}
                sizes="100vw"
                {...DIMENSION_XS}
                {...ueProps}
              />
            )}
          </UeElement>
        </picture>
        <StyledLayout ref={textboxRef}>
          <StyledLayoutItem>
            <Headline
              tag="h2"
              variant={{
                type: 'headline',
                order: '2',
                weight: process.env.IS_LEGACY ? 'bold' : 'normal',
              }}
              data-testid="headline"
            >
              <UeElement type="text" property="headline" label="Headline">
                {headline}
              </UeElement>
            </Headline>
            <Text variant={{ type: 'copy', order: '1', style: 'normal' }} data-testid="copy-text">
              <UeElement type="text" property="copy" label="Copy Text">
                {copy}
              </UeElement>
            </Text>
            {links && links?.length > 0 && (
              <Layout direction={{ xs: 'column', md: 'row' }} rowGap={gap} columnGap={gap}>
                {linkType === 'link' &&
                  links.map(({ href, text, ctaAriaLabel }) => (
                    <TextLink
                      href={href}
                      key={`${ctaAriaLabel}`}
                      onClick={() => linkTracking && linkTracking(href, text, '')}
                      data-testid={`cta-${ctaAriaLabel}`}
                      aria-label={ctaAriaLabel}
                      size="md"
                      variant="standalone"
                    >
                      <UeElement type="text" property={`links_${ctaAriaLabel}_text`} label="Link">
                        {text}
                      </UeElement>
                    </TextLink>
                  ))}
                <ButtonGroup>
                  {links[0] !== undefined && linkType === 'button' && (
                    <StyledTextButton
                      href={links[0].href}
                      variant="primary"
                      // eslint-disable-next-line react/no-array-index-key
                      key={0}
                      onClick={() => linkTracking && linkTracking(links[0].href, links[0].text, '')}
                      data-testid="cta-0"
                      size={process.env.IS_LEGACY ? 'md' : 'sm'}
                      aria-label={links[0].ctaAriaLabel}
                    >
                      <UeElement type="text" property="links_0_text" label="Link">
                        {links[0].text}
                      </UeElement>
                    </StyledTextButton>
                  )}
                  {links[1] !== undefined && linkType === 'button' && (
                    <StyledTextButton
                      href={links[1].href}
                      variant="secondary"
                      // eslint-disable-next-line react/no-array-index-key
                      key={1}
                      onClick={() => linkTracking && linkTracking(links[1].href, links[1].text, '')}
                      data-testid="cta-1"
                      size={process.env.IS_LEGACY ? 'md' : 'sm'}
                      aria-label={links[1].ctaAriaLabel}
                    >
                      <UeElement type="text" property="links_1_text" label="Link">
                        {links[1].text}
                      </UeElement>
                    </StyledTextButton>
                  )}
                </ButtonGroup>
              </Layout>
            )}
          </StyledLayoutItem>
        </StyledLayout>
      </ParallaxArea>
      {(legalData.wltpData.length > 0 || legalData.additionalText) && (
        <ThemeProvider theme={getThemeName(theme)}>
          <LegalArea data-testid="legal-text-area">
            <LegalAreaItem>
              <Text variant={{ type: 'copy', order: '2', style: 'normal' }}>
                {legalData.wltpData &&
                  legalData.wltpData.map((data, index) => (
                    <WltpValuesComponent {...data} key={`${index.toString}`} />
                  ))}
                {legalData.additionalText && (
                  <WltpSpan data-testid="additional-legal-text">
                    <UeElement
                      type="text"
                      property="legalData_additionalText"
                      label="Additional Legal Text"
                    >
                      {legalData.additionalText}
                    </UeElement>
                  </WltpSpan>
                )}
              </Text>
            </LegalAreaItem>
          </LegalArea>
        </ThemeProvider>
      )}
    </ParallaxTeaser>
  );
};
